import { DataProvider, HttpError } from 'react-admin';
import { apiClient, ApiError, redactSensitiveForLog } from './utils/apiClient';
import { API_CONFIG } from './config/api';

// Helper to convert API errors to HttpError with translation keys
const handleApiError = (error: any): never => {
    if (error && typeof error === 'object' && 'status' in error) {
        const apiError = error as ApiError;
        // Use error code for translation if available, otherwise use message
        const message = apiError.errorCode 
            ? `errors.${apiError.errorCode}` 
            : apiError.message;
        
        throw new HttpError(message, apiError.status, apiError);
    }
    // For non-API errors, throw as-is
    throw error;
};

export const dataProvider: DataProvider = {
    getList: async (resource, params) => {
        try {
            // Support custom paths via meta
            if (params.meta?.path) {
                const response: any = await apiClient.get(`/api/backend/${params.meta.path}`);
                return {
                    data: response.data || response,
                    total: response.total || (Array.isArray(response) ? response.length : 0),
                };
            }

            const { page, perPage } = params.pagination || { page: 1, perPage: 10 };
            const { field, order } = params.sort || { field: 'id', order: 'ASC' };
            const offset = (page - 1) * perPage;
            // Send EVERY pagination convention the various backend handlers
            // accept. Different handlers in this codebase read from different
            // param names (page/perPage, limit/offset, _start/_end). The
            // default Zod schemas strip unknown keys, the parseInt-based
            // handlers ignore unknown keys, so duplicating the values is safe
            // and avoids a long list of per-resource special cases.
            const query = {
                ...params.filter,
                page: page,
                perPage: perPage,
                limit: perPage,
                offset: offset,
                _start: offset,
                _end: offset + perPage,
                sort: field,
                order: order.toLowerCase(), // Convert to lowercase for API compatibility
            };

            // Build query string
            const queryString = Object.entries(query)
                .filter(([_, value]) => value !== undefined && value !== '')
                .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
                .join('&');

            // Special handling for notification group assignments
            if (resource === 'notification-groups-assignments' && params.filter?.groupId) {
                const groupId = params.filter.groupId;
                const response: any = await apiClient.get(
                    `${API_CONFIG.endpoints.backend.notificationGroups}/${groupId}/assignments`
                );
                return {
                    data: response.data || [],
                    total: response.total || 0,
                };
            }

            // Special handling for lms-logs (uses different parameter names)
            if (resource === 'lms-logs') {
                const lmsQuery = {
                    ...params.filter,
                    page: page,
                    limit: perPage, // Backend expects 'limit' not 'perPage'
                    sort: field,
                    order: order.toLowerCase(),
                };

                const lmsQueryString = Object.entries(lmsQuery)
                    .filter(([_, value]) => value !== undefined && value !== '')
                    .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
                    .join('&');

                const response: any = await apiClient.get(
                    `${API_CONFIG.endpoints.backend.lmsLogs}?${lmsQueryString}`
                );

                return {
                    data: response.data || [],
                    total: response.total || 0,
                };
            }

            // Special handling for audit logs
            if (resource === 'audit-logs/stats') {
                const response = await apiClient.get(
                    `${API_CONFIG.endpoints.backend.auditLogs}/stats?${queryString}`
                );
                return {
                    data: response,
                    total: 1,
                };
            }

            if (resource === 'audit-logs') {
                const response: any = await apiClient.get(
                    `${API_CONFIG.endpoints.backend.auditLogs}?${queryString}`
                );
                return {
                    data: response.data,
                    total: response.total,
                };
            }

            // Special handling for ocpp-servers/:id/chargers
            if (resource.startsWith('ocpp-servers/') && resource.endsWith('/chargers')) {
                const response: any = await apiClient.get(`/api/backend/${resource}`);
                return {
                    data: response.data || [],
                    total: response.total || 0,
                };
            }

            // Special handling for lms-templates dropdown
            if (resource === 'lms-templates' && params.filter?.all === 'true') {
                // Build custom query for dropdown usage
                const dropdownQuery = {
                    all: 'true',
                    sort: field === 'id' ? 'name' : field, // Default to name sort
                    order: order.toLowerCase(),
                    ...(params.filter.search && { search: params.filter.search })
                };

                const dropdownQueryString = Object.entries(dropdownQuery)
                    .filter(([_, value]) => value !== undefined && value !== '')
                    .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
                    .join('&');

                const response: any = await apiClient.get(`/api/lms-templates?${dropdownQueryString}`);

                // Handle response as array
                if (Array.isArray(response)) {
                    return {
                        data: response,
                        total: response.length,
                    };
                }

                return {
                    data: response.data || response,
                    total: response.total || (response.data ? response.data.length : 0),
                };
            }


            // Map resources to backend endpoints
            const resourceMap: Record<string, string> = {
                'backend-users': API_CONFIG.endpoints.backend.users,
                'roles': API_CONFIG.endpoints.backend.roles,
                'settings': API_CONFIG.endpoints.backend.settings,
                'fee-templates': API_CONFIG.endpoints.backend.feeTemplates,
                'fee-schedules': API_CONFIG.endpoints.backend.feeSchedules,
                'fee-template-changes': API_CONFIG.endpoints.backend.feeTemplateScheduledChanges,
                'sites': API_CONFIG.endpoints.backend.sites,
                'chargers': API_CONFIG.endpoints.backend.chargers,
                'charger-types': API_CONFIG.endpoints.backend.chargerTypes,
                'load-groups': API_CONFIG.endpoints.backend.loadGroups,
                'lms-templates': API_CONFIG.endpoints.backend.lmsTemplates,
                'lms-logs': API_CONFIG.endpoints.backend.lmsLogs,
                'ocpp-templates': API_CONFIG.endpoints.backend.ocppTemplates,
                'ocpp-servers': API_CONFIG.endpoints.backend.ocppServers,
                'ocpp-logs': '/api/backend/ocpp-logs',
                'supabase-connections': API_CONFIG.endpoints.backend.supabaseConnections,
                'notification-groups': API_CONFIG.endpoints.backend.notificationGroups,
                'notification-logs': API_CONFIG.endpoints.backend.notificationLogs,
                'messages': API_CONFIG.endpoints.backend.messages,
                'banners': API_CONFIG.endpoints.backend.banners,
                'promotions': '/api/backend/promotions',
                'app-users': API_CONFIG.endpoints.backend.appUsers,
                'transactions': API_CONFIG.endpoints.transactions,
                'reservations': '/api/backend/reservations',
                'vendors': '/api/backend/vendors',
                'kiosk-devices': API_CONFIG.endpoints.backend.kioskDevices,
                'rfid-users': '/api/backend/rfid-users',
                'rfid-cards': '/api/backend/rfid-cards',
                'app-transactions': '/api/backend/app-transactions',
                'app-topups': '/api/backend/app-topups',
                'app-topup-attempts': '/api/backend/app-topup-attempts',
                'account-deletion-requests': '/api/backend/account-deletion-requests',
                'rfid-transactions': '/api/backend/rfid-transactions',
                'kiosk-transactions': '/api/backend/kiosk-transactions',
                'kiosk-transactions-needs-action': '/api/backend/kiosk-transactions/needs-action',
                'kiosk-settlements': '/api/backend/kiosk-settlements',
                'kiosk-alerts': '/api/backend/kiosk-alerts',
                'kiosk-releases': '/api/backend/kiosk-releases',
                'kiosk-groups': '/api/backend/kiosk-groups',
                'active-sessions': '/api/backend/active-sessions',
                'charging-sessions': '/api/backend/charging-sessions',
                'charger-firmware': '/api/backend/charger-firmware',
                'firmware-updates': '/api/backend/firmware-updates',
                'payment-methods': '/api/backend/payment-methods',
                'payment-gateways': '/api/backend/payment-gateways',
                'vehicle-brands': '/api/backend/vehicle-brands',
                'vehicle-models': '/api/backend/vehicle-models',
                'app-versions': '/api/backend/app-versions',
                'user-groups': '/api/backend/user-groups',
                'reservation-policies': '/api/backend/reservation-policies',
                'ai-playbooks': '/api/backend/ai-troubleshooting/playbooks',
                'ai-model-configs': '/api/backend/ai-troubleshooting/model-configs',
            };

            const url = resourceMap[resource] || `/api/${resource}`;
            const response: any = await apiClient.get(`${url}?${queryString}`);

            // Handle different response formats
            if (Array.isArray(response)) {
                return {
                    data: response,
                    total: response.length,
                };
            }

            if (response.data && response.total !== undefined) {
                // Normalize charger status to lowercase
                if (resource === 'chargers' && Array.isArray(response.data)) {
                    response.data = response.data.map((charger: any) => ({
                        ...charger,
                        status: charger.status?.toLowerCase() || 'offline'
                    }));
                }
                
                return {
                    data: response.data,
                    total: response.total,
                };
            }

            return {
                data: response,
                total: 0,
            };
        } catch (error) {
            return handleApiError(error);
        }
    },

    getOne: async (resource, params) => {
        try {
            const resourceMap: Record<string, string> = {
                'backend-users': API_CONFIG.endpoints.backend.users,
                'roles': API_CONFIG.endpoints.backend.roles,
                'fee-templates': API_CONFIG.endpoints.backend.feeTemplates,
                'fee-schedules': API_CONFIG.endpoints.backend.feeSchedules,
                'fee-template-changes': API_CONFIG.endpoints.backend.feeTemplateScheduledChanges,
                'sites': API_CONFIG.endpoints.backend.sites,
                'chargers': API_CONFIG.endpoints.backend.chargers,
                'charger-types': API_CONFIG.endpoints.backend.chargerTypes,
                'load-groups': API_CONFIG.endpoints.backend.loadGroups,
                'lms-templates': API_CONFIG.endpoints.backend.lmsTemplates,
                'lms-logs': API_CONFIG.endpoints.backend.lmsLogs,
                'ocpp-templates': API_CONFIG.endpoints.backend.ocppTemplates,
                'ocpp-servers': API_CONFIG.endpoints.backend.ocppServers,
                'supabase-connections': API_CONFIG.endpoints.backend.supabaseConnections,
                'notification-groups': API_CONFIG.endpoints.backend.notificationGroups,
                'notification-logs': API_CONFIG.endpoints.backend.notificationLogs,
                'messages': API_CONFIG.endpoints.backend.messages,
                'banners': API_CONFIG.endpoints.backend.banners,
                'promotions': '/api/backend/promotions',
                'app-users': API_CONFIG.endpoints.backend.appUsers,
                'vendors': '/api/backend/vendors',
                'kiosk-devices': API_CONFIG.endpoints.backend.kioskDevices,
                'rfid-users': '/api/backend/rfid-users',
                'rfid-cards': '/api/backend/rfid-cards',
                'app-transactions': '/api/backend/app-transactions',
                'app-topups': '/api/backend/app-topups',
                'app-topup-attempts': '/api/backend/app-topup-attempts',
                'account-deletion-requests': '/api/backend/account-deletion-requests',
                'rfid-transactions': '/api/backend/rfid-transactions',
                'kiosk-transactions': '/api/backend/kiosk-transactions',
                'kiosk-alerts': '/api/backend/kiosk-alerts',
                'kiosk-releases': '/api/backend/kiosk-releases',
                'kiosk-groups': '/api/backend/kiosk-groups',
                'active-sessions': '/api/backend/active-sessions',
                'charging-sessions': '/api/backend/charging-sessions',
                'charger-firmware': '/api/backend/charger-firmware',
                'firmware-updates': '/api/backend/firmware-updates',
                'payment-methods': '/api/backend/payment-methods',
                'payment-gateways': '/api/backend/payment-gateways',
                'vehicle-brands': '/api/backend/vehicle-brands',
                'vehicle-models': '/api/backend/vehicle-models',
                'app-versions': '/api/backend/app-versions',
                'user-groups': '/api/backend/user-groups',
                'reservation-policies': '/api/backend/reservation-policies',
                'reservations': '/api/backend/reservations',
                'ai-playbooks': '/api/backend/ai-troubleshooting/playbooks',
                'ai-model-configs': '/api/backend/ai-troubleshooting/model-configs',
            };

            const url = resourceMap[resource] || `/api/${resource}`;
            const response: any = await apiClient.get(`${url}/${params.id}`);

            // Special handling for OCPP logs endpoint (nested path with query params)
            if (resource === 'charging-sessions' && String(params.id).includes('/ocpp-logs')) {
                // For OCPP logs, the response is { data: [...], total: 37, ... }
                // We need to return the entire response wrapped in data, with an id field
                return { data: { ...response, id: params.id } };
            }

            // Handle endpoints that return { data: item } format
            if ((resource === 'sites' || resource === 'fee-templates' ||
                 resource === 'fee-schedules' ||
                 resource === 'fee-template-changes' || resource === 'chargers' ||
                 resource === 'charger-types' || resource === 'lms-templates' ||
                 resource === 'lms-logs' ||
                 resource === 'ocpp-templates' || resource === 'ocpp-servers' || resource === 'load-groups' ||
                 resource === 'supabase-connections' || resource === 'notification-groups' || resource === 'notification-logs' ||
                 resource === 'messages' || resource === 'banners' || resource === 'promotions' ||
                 resource === 'vendors' || resource === 'kiosk-devices' || resource === 'rfid-users' || resource === 'rfid-cards' ||
                 resource === 'app-users' || resource === 'app-transactions' || resource === 'app-topups' || resource === 'app-topup-attempts' || resource === 'rfid-transactions' || resource === 'kiosk-transactions' || resource === 'kiosk-alerts' || resource === 'kiosk-releases' || resource === 'kiosk-groups' ||
                 resource === 'active-sessions' || resource === 'charging-sessions' ||
                 resource === 'charger-firmware' || resource === 'firmware-updates' ||
                 resource === 'payment-methods' || resource === 'payment-gateways' ||
                 resource === 'account-deletion-requests' ||
                 resource === 'vehicle-brands' || resource === 'vehicle-models' ||
                 resource === 'app-versions' ||
                 resource === 'user-groups' || resource === 'reservation-policies' || resource === 'reservations' ||
                 resource === 'ai-playbooks' || resource === 'ai-model-configs') && response.data) {
                // Ensure the data has an id field for React Admin
                const data = response.data;
                if (!data.id && params.id) {
                    data.id = params.id;
                }

                // Normalize charger status to lowercase
                if (resource === 'chargers' && data.status) {
                    data.status = data.status.toLowerCase();
                }

                return { data };
            }
            
            // For other responses, ensure they have an id field
            if (!response.id && params.id) {
                response.id = params.id;
            }
            
            return { data: response };
        } catch (error) {
            return handleApiError(error);
        }
    },

    getMany: async (resource, params) => {
        const promises = params.ids.map(id => dataProvider.getOne(resource, { id }));
        const responses = await Promise.all(promises);
        return { data: responses.map(r => r.data) };
    },

    getManyReference: async (resource, params) => {
        try {
            const { page, perPage } = params.pagination;
            const { field, order } = params.sort;
            const query = {
                ...params.filter,
                [params.target]: params.id,
                page: page,
                perPage: perPage,
                sort: field,
                order: order.toLowerCase(), // Convert to lowercase for API compatibility
            };

            const queryString = Object.entries(query)
                .filter(([_, value]) => value !== undefined)
                .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
                .join('&');

            // Map resources to backend endpoints (mirrors getList)
            const resourceMap: Record<string, string> = {
                'backend-users': API_CONFIG.endpoints.backend.users,
                'roles': API_CONFIG.endpoints.backend.roles,
                'settings': API_CONFIG.endpoints.backend.settings,
                'fee-templates': API_CONFIG.endpoints.backend.feeTemplates,
                'fee-schedules': API_CONFIG.endpoints.backend.feeSchedules,
                'fee-template-changes': API_CONFIG.endpoints.backend.feeTemplateScheduledChanges,
                'sites': API_CONFIG.endpoints.backend.sites,
                'chargers': API_CONFIG.endpoints.backend.chargers,
                'charger-types': API_CONFIG.endpoints.backend.chargerTypes,
                'load-groups': API_CONFIG.endpoints.backend.loadGroups,
                'lms-templates': API_CONFIG.endpoints.backend.lmsTemplates,
                'lms-logs': API_CONFIG.endpoints.backend.lmsLogs,
                'ocpp-templates': API_CONFIG.endpoints.backend.ocppTemplates,
                'ocpp-servers': API_CONFIG.endpoints.backend.ocppServers,
                'ocpp-logs': '/api/backend/ocpp-logs',
                'supabase-connections': API_CONFIG.endpoints.backend.supabaseConnections,
                'notification-groups': API_CONFIG.endpoints.backend.notificationGroups,
                'notification-logs': API_CONFIG.endpoints.backend.notificationLogs,
                'messages': API_CONFIG.endpoints.backend.messages,
                'banners': API_CONFIG.endpoints.backend.banners,
                'promotions': '/api/backend/promotions',
                'app-users': API_CONFIG.endpoints.backend.appUsers,
                'transactions': API_CONFIG.endpoints.transactions,
                'reservations': '/api/backend/reservations',
                'vendors': '/api/backend/vendors',
                'kiosk-devices': API_CONFIG.endpoints.backend.kioskDevices,
                'rfid-users': '/api/backend/rfid-users',
                'rfid-cards': '/api/backend/rfid-cards',
                'app-transactions': '/api/backend/app-transactions',
                'app-topups': '/api/backend/app-topups',
                'app-topup-attempts': '/api/backend/app-topup-attempts',
                'account-deletion-requests': '/api/backend/account-deletion-requests',
                'rfid-transactions': '/api/backend/rfid-transactions',
                'kiosk-transactions': '/api/backend/kiosk-transactions',
                'active-sessions': '/api/backend/active-sessions',
                'charging-sessions': '/api/backend/charging-sessions',
                'charger-firmware': '/api/backend/charger-firmware',
                'firmware-updates': '/api/backend/firmware-updates',
                'payment-methods': '/api/backend/payment-methods',
                'payment-gateways': '/api/backend/payment-gateways',
                'vehicle-brands': '/api/backend/vehicle-brands',
                'vehicle-models': '/api/backend/vehicle-models',
                'app-versions': '/api/backend/app-versions',
                'user-groups': '/api/backend/user-groups',
                'reservation-policies': '/api/backend/reservation-policies',
                'ai-playbooks': '/api/backend/ai-troubleshooting/playbooks',
                'ai-model-configs': '/api/backend/ai-troubleshooting/model-configs',
            };

            const url = resourceMap[resource] || `/api/${resource}`;
            const response: any = await apiClient.get(`${url}?${queryString}`);

            if (Array.isArray(response)) {
                return {
                    data: response,
                    total: response.length,
                };
            }

            return {
                data: response.data || response,
                total: response.total || 0,
            };
        } catch (error) {
            return handleApiError(error);
        }
    },

    create: async (resource, params) => {
        try {
            // Support custom paths via meta
            if (params.meta?.path) {
                const response: any = await apiClient.post(`/api/backend/${params.meta.path}`, params.data);
                // Backend returns { data: {...} }, extract it
                const responseData = response.data || response;
                return { data: responseData };
            }

            // Special handling for OCPP server regenerate endpoints
            if (resource.startsWith('ocpp-servers/') && resource.includes('/regenerate-api-key')) {
                const match = resource.match(/ocpp-servers\/(.+)\/regenerate-api-key/);
                if (match) {
                    const serverId = match[1];
                    const response: any = await apiClient.post(
                        `${API_CONFIG.endpoints.backend.ocppServers}/${serverId}/regenerate-api-key`,
                        params.data
                    );
                    // Backend returns { data: { apiKey, message } }, we need to extract it
                    const responseData = response.data || response;
                    return { data: { ...responseData, id: serverId } };
                }
            }

            if (resource.startsWith('ocpp-servers/') && resource.includes('/regenerate-webhook-secret')) {
                const match = resource.match(/ocpp-servers\/(.+)\/regenerate-webhook-secret/);
                if (match) {
                    const serverId = match[1];
                    const response: any = await apiClient.post(
                        `${API_CONFIG.endpoints.backend.ocppServers}/${serverId}/regenerate-webhook-secret`,
                        params.data
                    );
                    // Backend returns { data: { webhookSecret, message } }, we need to extract it
                    const responseData = response.data || response;
                    return { data: { ...responseData, id: serverId } };
                }
            }

            // Special handling for notification group members
            if (resource.startsWith('notification-groups/') && resource.includes('/members')) {
                const match = resource.match(/notification-groups\/(.+)\/members/);
                if (match) {
                    const groupId = match[1];
                    const response: any = await apiClient.post(
                        `${API_CONFIG.endpoints.backend.notificationGroups}/${groupId}/members`,
                        params.data
                    );
                    // React Admin expects an object with an id field
                    const responseData = response.data || response;
                    if (!responseData.id) {
                        // Create a synthetic id for the response
                        responseData.id = `${groupId}_members_${Date.now()}`;
                    }
                    return { data: responseData };
                }
            }

            // Special handling for notification group assignments
            if (resource === 'notification-groups-assignments') {
                const { groupId, ...assignmentData } = params.data;
                const response: any = await apiClient.post(
                    `${API_CONFIG.endpoints.backend.notificationGroups}/${groupId}/assignments`,
                    assignmentData
                );
                return { data: response.data || response };
            }

            const resourceMap: Record<string, string> = {
                'backend-users': API_CONFIG.endpoints.backend.users,
                'roles': API_CONFIG.endpoints.backend.roles,
                'fee-templates': API_CONFIG.endpoints.backend.feeTemplates,
                'fee-schedules': API_CONFIG.endpoints.backend.feeSchedules,
                'fee-template-changes': API_CONFIG.endpoints.backend.feeTemplateScheduledChanges,
                'sites': API_CONFIG.endpoints.backend.sites,
                'chargers': API_CONFIG.endpoints.backend.chargers,
                'charger-types': API_CONFIG.endpoints.backend.chargerTypes,
                'load-groups': API_CONFIG.endpoints.backend.loadGroups,
                'lms-templates': API_CONFIG.endpoints.backend.lmsTemplates,
                'ocpp-templates': API_CONFIG.endpoints.backend.ocppTemplates,
                'ocpp-servers': API_CONFIG.endpoints.backend.ocppServers,
                'supabase-connections': API_CONFIG.endpoints.backend.supabaseConnections,
                'notification-groups': API_CONFIG.endpoints.backend.notificationGroups,
                'notification-logs': API_CONFIG.endpoints.backend.notificationLogs,
                'messages': API_CONFIG.endpoints.backend.messages,
                'banners': API_CONFIG.endpoints.backend.banners,
                'promotions': '/api/backend/promotions',
                'app-users': API_CONFIG.endpoints.backend.appUsers,
                'vendors': '/api/backend/vendors',
                'kiosk-devices': API_CONFIG.endpoints.backend.kioskDevices,
                'rfid-users': '/api/backend/rfid-users',
                'rfid-cards': '/api/backend/rfid-cards',
                'app-transactions': '/api/backend/app-transactions',
                'app-topups': '/api/backend/app-topups',
                'app-topup-attempts': '/api/backend/app-topup-attempts',
                'account-deletion-requests': '/api/backend/account-deletion-requests',
                'rfid-transactions': '/api/backend/rfid-transactions',
                'kiosk-transactions': '/api/backend/kiosk-transactions',
                'active-sessions': '/api/backend/active-sessions',
                'charging-sessions': '/api/backend/charging-sessions',
                'charger-firmware': '/api/backend/charger-firmware',
                'firmware-updates': '/api/backend/firmware-updates',
                'payment-methods': '/api/backend/payment-methods',
                'payment-gateways': '/api/backend/payment-gateways',
                'vehicle-brands': '/api/backend/vehicle-brands',
                'vehicle-models': '/api/backend/vehicle-models',
                'app-versions': '/api/backend/app-versions',
                'user-groups': '/api/backend/user-groups',
                'reservation-policies': '/api/backend/reservation-policies',
                'reservations': '/api/backend/reservations',
                'ai-playbooks': '/api/backend/ai-troubleshooting/playbooks',
                'ai-model-configs': '/api/backend/ai-troubleshooting/model-configs',
            };

            const url = resourceMap[resource] || `/api/${resource}`;

            // Transform data for chargers to ensure proper types
            let createData = params.data;
            if (resource === 'chargers') {
                createData = {
                    ...params.data,
                    // Ensure boolean values are properly converted
                    authEnabled: params.data.authEnabled === true || params.data.authEnabled === 'true',
                    // Ensure numeric values are properly converted
                    maxPower: params.data.maxPower ? Number(params.data.maxPower) : undefined,
                    connectorCount: params.data.connectorCount ? Number(params.data.connectorCount) : undefined,
                };
            }

            // Log banner data before sending
            if (resource === 'banners') {
                console.log('[DataProvider] Creating banner with data:', JSON.stringify(params.data, null, 2));
                console.log('[DataProvider] Translations in request:', params.data.translations);
            }

            // Transform data for messages to ensure proper types
            if (resource === 'messages') {
                createData = {
                    ...params.data,
                    // Parse JSON strings to proper types
                    tags: typeof params.data.tags === 'string'
                        ? (params.data.tags === '[]' ? [] : JSON.parse(params.data.tags))
                        : params.data.tags || [],
                    push_navigation_params: typeof params.data.push_navigation_params === 'string'
                        ? (params.data.push_navigation_params === '{}' ? {} : JSON.parse(params.data.push_navigation_params))
                        : params.data.push_navigation_params || {},
                    // Ensure target_data is properly structured
                    target_data: params.data.target_data || {
                        membership_levels: params.data['target_data.membership_levels'] || [],
                        user_ids: params.data['target_data.user_ids'] || []
                    }
                };
                // Remove the old field names if they exist
                delete createData['target_data.membership_levels'];
                delete createData['target_data.user_ids'];
            }
            
            // Note: fee-schedules entity transformation is handled in the form's transform function
            
            const response: any = await apiClient.post(url, createData);
            
            console.log('[DataProvider] Create response for', resource, ':', redactSensitiveForLog(response));
            
            // Handle endpoints which return { data: item }
            if ((resource === 'sites' || resource === 'chargers' || resource === 'charger-types' ||
                 resource === 'load-groups' || resource === 'fee-schedules' || resource === 'fee-templates' ||
                 resource === 'ocpp-servers' || resource === 'supabase-connections' || resource === 'notification-groups' ||
                 resource === 'messages' || resource === 'banners' || resource === 'promotions' ||
                 resource === 'app-users' || resource === 'vendors' || resource === 'kiosk-devices' || resource === 'rfid-users' || resource === 'rfid-cards' ||
                 resource === 'charger-firmware' || resource === 'firmware-updates' ||
                 resource === 'vehicle-brands' || resource === 'vehicle-models' ||
                 resource === 'app-versions' ||
                 resource === 'user-groups' || resource === 'reservation-policies' ||
                 resource === 'ai-playbooks' || resource === 'ai-model-configs') && response?.data) {
                console.log('[DataProvider] Returning data:', redactSensitiveForLog(response.data));
                return { data: response.data };
            }
            
            // For other resources, ensure we have an id
            const responseId = response.id || response.data?.id;
            console.log('[DataProvider] Create response without data wrapper, id:', responseId);
            
            return { data: { ...params.data, id: responseId } as any };
        } catch (error) {
            return handleApiError(error);
        }
    },

    update: async (resource, params) => {
        try {
            // Special handling for notification group members
            if (resource.startsWith('notification-groups/') && resource.includes('/members')) {
                const match = resource.match(/notification-groups\/(.+)\/members/);
                if (match) {
                    const groupId = match[1];
                    const response: any = await apiClient.put(
                        `${API_CONFIG.endpoints.backend.notificationGroups}/${groupId}/members/${params.id}`,
                        params.data
                    );
                    return { data: response.data || response };
                }
            }
            
            // Special handling for notification group assignments
            if (resource === 'notification-groups-assignments') {
                const { groupId, id, ...assignmentData } = params.data;
                const response: any = await apiClient.put(
                    `${API_CONFIG.endpoints.backend.notificationGroups}/${groupId}/assignments/${id}`,
                    assignmentData
                );
                return { data: response.data || response };
            }

            // Special handling for settings
            if (resource === 'settings') {
                await apiClient.put(
                    `${API_CONFIG.endpoints.backend.settings}/${params.id}`,
                    params.data
                );
                return { data: { ...params.data, id: params.id } as any };
            }

            const resourceMap: Record<string, string> = {
                'backend-users': API_CONFIG.endpoints.backend.users,
                'roles': API_CONFIG.endpoints.backend.roles,
                'fee-templates': API_CONFIG.endpoints.backend.feeTemplates,
                'fee-schedules': API_CONFIG.endpoints.backend.feeSchedules,
                'fee-template-changes': API_CONFIG.endpoints.backend.feeTemplateScheduledChanges,
                'sites': API_CONFIG.endpoints.backend.sites,
                'chargers': API_CONFIG.endpoints.backend.chargers,
                'charger-types': API_CONFIG.endpoints.backend.chargerTypes,
                'load-groups': API_CONFIG.endpoints.backend.loadGroups,
                'lms-templates': API_CONFIG.endpoints.backend.lmsTemplates,
                'ocpp-templates': API_CONFIG.endpoints.backend.ocppTemplates,
                'ocpp-servers': API_CONFIG.endpoints.backend.ocppServers,
                'supabase-connections': API_CONFIG.endpoints.backend.supabaseConnections,
                'notification-groups': API_CONFIG.endpoints.backend.notificationGroups,
                'notification-logs': API_CONFIG.endpoints.backend.notificationLogs,
                'messages': API_CONFIG.endpoints.backend.messages,
                'banners': API_CONFIG.endpoints.backend.banners,
                'promotions': '/api/backend/promotions',
                'app-users': API_CONFIG.endpoints.backend.appUsers,
                'vendors': '/api/backend/vendors',
                'kiosk-devices': API_CONFIG.endpoints.backend.kioskDevices,
                'rfid-users': '/api/backend/rfid-users',
                'rfid-cards': '/api/backend/rfid-cards',
                'app-transactions': '/api/backend/app-transactions',
                'app-topups': '/api/backend/app-topups',
                'app-topup-attempts': '/api/backend/app-topup-attempts',
                'account-deletion-requests': '/api/backend/account-deletion-requests',
                'rfid-transactions': '/api/backend/rfid-transactions',
                'kiosk-transactions': '/api/backend/kiosk-transactions',
                'active-sessions': '/api/backend/active-sessions',
                'charging-sessions': '/api/backend/charging-sessions',
                'charger-firmware': '/api/backend/charger-firmware',
                'firmware-updates': '/api/backend/firmware-updates',
                'payment-methods': '/api/backend/payment-methods',
                'payment-gateways': '/api/backend/payment-gateways',
                'vehicle-brands': '/api/backend/vehicle-brands',
                'vehicle-models': '/api/backend/vehicle-models',
                'app-versions': '/api/backend/app-versions',
                'user-groups': '/api/backend/user-groups',
                'reservation-policies': '/api/backend/reservation-policies',
                'reservations': '/api/backend/reservations',
                'ai-playbooks': '/api/backend/ai-troubleshooting/playbooks',
                'ai-model-configs': '/api/backend/ai-troubleshooting/model-configs',
            };

            const url = resourceMap[resource] || `/api/${resource}`;

            // Transform data for chargers to ensure proper types
            let updateData = params.data;
            if (resource === 'chargers') {
                console.log('[DataProvider] Original charger update data:', params.data);
                updateData = {
                    ...params.data,
                    // Ensure boolean values are properly converted
                    authEnabled: params.data.authEnabled === true || params.data.authEnabled === 'true',
                    // Ensure numeric values are properly converted
                    maxPower: params.data.maxPower ? Number(params.data.maxPower) : undefined,
                    connectorCount: params.data.connectorCount ? Number(params.data.connectorCount) : undefined,
                    // Keep status in lowercase for consistency (backend will handle case)
                    status: params.data.status?.toLowerCase() || undefined,
                };
                console.log('[DataProvider] Transformed charger update data:', updateData);
            }

            // Log banner data before sending
            if (resource === 'banners') {
                console.log('[DataProvider] Updating banner with data:', JSON.stringify(params.data, null, 2));
                console.log('[DataProvider] Translations in update:', params.data.translations);
            }

            // Transform data for messages to ensure proper types
            if (resource === 'messages') {
                console.log('[DataProvider] Original message update data:', params.data);
                console.log('[DataProvider] resend_notification value:', params.data.resend_notification);
                updateData = {
                    ...params.data,
                    // Parse JSON strings to proper types
                    tags: typeof params.data.tags === 'string'
                        ? (params.data.tags === '[]' ? [] : JSON.parse(params.data.tags))
                        : params.data.tags || [],
                    push_navigation_params: typeof params.data.push_navigation_params === 'string'
                        ? (params.data.push_navigation_params === '{}' ? {} : JSON.parse(params.data.push_navigation_params))
                        : params.data.push_navigation_params || {},
                    // Ensure target_data is properly structured
                    target_data: params.data.target_data || {
                        membership_levels: params.data.target_membership_levels || [],
                        user_ids: params.data.target_user_ids || []
                    }
                };
                // Remove the old field names if they exist
                delete updateData.target_membership_levels;
                delete updateData.target_user_ids;
                console.log('[DataProvider] Final message update data being sent:', updateData);
                console.log('[DataProvider] resend_notification in final data:', updateData.resend_notification);
            }
            
            // Note: fee-schedules entity transformation is handled in the form's transform function
            
            // Use POST for fee-template-changes cancel action to work around Wrangler CORS issue
            if (resource === 'fee-template-changes' && params.data.status === 'cancelled') {
                console.log(`[DataProvider] Using POST for cancel action on ${resource} to ${url}/${params.id}/cancel`);
                const response: any = await apiClient.post(`${url}/${params.id}/cancel`, {});
                return { data: response.data || response };
            }
            
            // Use PATCH for certain resources
            const usePatch = resource === 'fee-template-changes' ||
                           resource === 'payment-methods' ||
                           resource === 'payment-gateways' ||
                           resource === 'reservations' ||
                           resource === 'reservation-policies' ||
                           resource === 'user-groups';
            if (usePatch) {
                console.log(`[DataProvider] Using PATCH for ${resource} to ${url}/${params.id}`, updateData);
            }
            const response: any = usePatch
                ? await apiClient.patch(`${url}/${params.id}`, updateData)
                : await apiClient.put(`${url}/${params.id}`, updateData);
            
            // Handle endpoints that return { data: item } format
            if ((resource === 'sites' || resource === 'fee-templates' ||
                 resource === 'fee-schedules' ||
                 resource === 'fee-template-changes' || resource === 'chargers' ||
                 resource === 'charger-types' || resource === 'lms-templates' ||
                 resource === 'ocpp-templates' || resource === 'ocpp-servers' || resource === 'load-groups' ||
                 resource === 'supabase-connections' || resource === 'notification-groups' || resource === 'messages' || resource === 'banners' || resource === 'promotions' ||
                 resource === 'app-users' || resource === 'vendors' || resource === 'kiosk-devices' || resource === 'rfid-users' || resource === 'rfid-cards' ||
                 resource === 'charger-firmware' || resource === 'firmware-updates' ||
                 resource === 'payment-methods' || resource === 'payment-gateways' ||
                 resource === 'vehicle-brands' || resource === 'vehicle-models' ||
                 resource === 'app-versions' ||
                 resource === 'user-groups' || resource === 'reservation-policies' || resource === 'reservations' ||
                 resource === 'ai-playbooks' || resource === 'ai-model-configs') && response?.data) {
                return { data: response.data };
            }

            return { data: { ...params.data, id: params.id } as any };
        } catch (error) {
            return handleApiError(error);
        }
    },

    updateMany: async (resource, params) => {
        const promises = params.ids.map(id =>
            dataProvider.update(resource, { 
                id, 
                data: params.data, 
                previousData: {} as any 
            })
        );
        const responses = await Promise.all(promises);
        return { data: responses.map(r => r.data.id) };
    },

    delete: async (resource, params) => {
        try {
            // Support custom paths via meta
            if (params.meta?.path) {
                await apiClient.delete(`/api/backend/${params.meta.path}`);
                return { data: params.previousData as any };
            }

            // Special handling for notification group members
            if (resource.startsWith('notification-groups/') && resource.includes('/members')) {
                const match = resource.match(/notification-groups\/(.+)\/members/);
                if (match) {
                    const groupId = match[1];
                    await apiClient.delete(
                        `${API_CONFIG.endpoints.backend.notificationGroups}/${groupId}/members/${params.id}`
                    );
                    return { data: params.previousData as any };
                }
            }
            
            // Special handling for notification group assignments
            if (resource === 'notification-groups-assignments') {
                const groupId = params.previousData?.groupId;
                const assignmentId = String(params.id);
                if (!groupId) {
                    throw new Error('Group ID is required for deleting assignments');
                }
                await apiClient.delete(
                    `${API_CONFIG.endpoints.backend.notificationGroups}/${groupId}/assignments/${assignmentId}`
                );
                return { data: params.previousData as any };
            }

            const resourceMap: Record<string, string> = {
                'backend-users': API_CONFIG.endpoints.backend.users,
                'roles': API_CONFIG.endpoints.backend.roles,
                'fee-templates': API_CONFIG.endpoints.backend.feeTemplates,
                'fee-schedules': API_CONFIG.endpoints.backend.feeSchedules,
                'fee-template-changes': API_CONFIG.endpoints.backend.feeTemplateScheduledChanges,
                'sites': API_CONFIG.endpoints.backend.sites,
                'chargers': API_CONFIG.endpoints.backend.chargers,
                'charger-types': API_CONFIG.endpoints.backend.chargerTypes,
                'load-groups': API_CONFIG.endpoints.backend.loadGroups,
                'lms-templates': API_CONFIG.endpoints.backend.lmsTemplates,
                'ocpp-templates': API_CONFIG.endpoints.backend.ocppTemplates,
                'ocpp-servers': API_CONFIG.endpoints.backend.ocppServers,
                'supabase-connections': API_CONFIG.endpoints.backend.supabaseConnections,
                'notification-groups': API_CONFIG.endpoints.backend.notificationGroups,
                'notification-logs': API_CONFIG.endpoints.backend.notificationLogs,
                'messages': API_CONFIG.endpoints.backend.messages,
                'banners': API_CONFIG.endpoints.backend.banners,
                'promotions': '/api/backend/promotions',
                'app-users': API_CONFIG.endpoints.backend.appUsers,
                'vendors': '/api/backend/vendors',
                'kiosk-devices': API_CONFIG.endpoints.backend.kioskDevices,
                'rfid-users': '/api/backend/rfid-users',
                'rfid-cards': '/api/backend/rfid-cards',
                'app-transactions': '/api/backend/app-transactions',
                'app-topups': '/api/backend/app-topups',
                'app-topup-attempts': '/api/backend/app-topup-attempts',
                'account-deletion-requests': '/api/backend/account-deletion-requests',
                'rfid-transactions': '/api/backend/rfid-transactions',
                'kiosk-transactions': '/api/backend/kiosk-transactions',
                'active-sessions': '/api/backend/active-sessions',
                'charging-sessions': '/api/backend/charging-sessions',
                'charger-firmware': '/api/backend/charger-firmware',
                'firmware-updates': '/api/backend/firmware-updates',
                'payment-methods': '/api/backend/payment-methods',
                'payment-gateways': '/api/backend/payment-gateways',
                'vehicle-brands': '/api/backend/vehicle-brands',
                'vehicle-models': '/api/backend/vehicle-models',
                'app-versions': '/api/backend/app-versions',
                'user-groups': '/api/backend/user-groups',
                'reservation-policies': '/api/backend/reservation-policies',
                'reservations': '/api/backend/reservations',
                'ai-playbooks': '/api/backend/ai-troubleshooting/playbooks',
                'ai-model-configs': '/api/backend/ai-troubleshooting/model-configs',
            };

            const url = resourceMap[resource] || `/api/${resource}`;
            await apiClient.delete(`${url}/${params.id}`);
            
            return { data: params.previousData as any };
        } catch (error) {
            return handleApiError(error);
        }
    },

    deleteMany: async (resource, params) => {
        const promises = params.ids.map(id =>
            dataProvider.delete(resource, { id, previousData: {} as any })
        );
        await Promise.all(promises);
        return { data: params.ids };
    },

    // Custom fetch method for reports and other custom endpoints
    customFetch: async (url: string, options?: RequestInit) => {
        try {
            const response = await apiClient.get(url);
            return response;
        } catch (error) {
            return handleApiError(error);
        }
    },
};